import { money } from './format.js';
import { BOARD_W } from './Whiteboard.jsx';

// The ruling under the cash-flow whiteboard: the household's own timeline,
// printed faintly so the advisor's marker reads as the drawing and this reads
// as the paper it was drawn on.
//
// Renders an SVG <g>, not a whole SVG, so the same element sits under the live
// canvas in the meeting and inside the saved sketch's SVG twin on the view link
// and the printer. The figures it is drawn from travel with the sketch, so a
// board reopened after the questionnaire changed is ruled as it was when the
// ink went on.
//
// Coordinates are the board's own 1568x760 space, so a stroke drawn across a
// figure on one screen lands across the same figure on every other. The board
// renders at roughly half that on a laptop, which is why nothing here is set
// smaller than 17px: at half scale that is the smallest type that still reads
// on a screen share.
//
// The top third is left empty on purpose. The advisor draws the story above
// the line, the way the training video does.

const X0 = 110;
const X1 = BOARD_W - 110;
const BASE = 420;

// Quieter than the four marker colours on purpose. The ruling is printed; the
// ink is drawn.
const INK = '#1a2040';
const GUIDE = '#7b8289';
const LINE = '#c5cad1';
const RED = '#c0392b';
const GREEN = '#1a7a4a';

const SMALL = 17;
const pct = r => `${(r * 100).toFixed(1)}%`;
const plural = (n, word) => `${n} ${n === 1 ? word : `${word}s`}`;

export default function CashFlowGuide({ model }) {
  if (!model || !Array.isArray(model.ticks) || model.ticks.length < 2) return null;
  const { years, ticks, segments, liquid, totals, decisions, hidden = [] } = model;
  const span = Math.max(1, years.last - years.first);
  const x = year => X0 + ((year - years.first) / span) * (X1 - X0);
  // The first and last labels hug the edges rather than hang off them.
  const anchorFor = i => (i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle');
  // Neighbouring moments can sit a few years apart with long names. The
  // middle ticks alternate between two rows so two labels never share one.
  const labelRow = i => (i === 0 || i === ticks.length - 1 ? 0 : (i - 1) % 2);

  return (
    <g className="asmt-guide" style={{ fontFamily: 'var(--sans)' }} opacity="0.92">
      <text x={X0} y={54} fontSize="34" fill={INK} style={{ fontFamily: 'var(--serif)' }}>
        Cash flow, year by year
      </text>
      <text x={X1} y={54} fontSize={SMALL} fill={GUIDE} textAnchor="end">
        No growth assumed. Every need is before tax and COLA.
      </text>
      {/* Moments the board had no room to label still happened. They are named
          here so nothing that changes the picture is hidden from the advisor. */}
      {hidden.length > 0 && (
        <text x={X1} y={78} fontSize={SMALL} fill={GUIDE} textAnchor="end">
          {`Also: ${hidden.map(h => `${h.label} (${h.year})`).join(', ')}`}
        </text>
      )}

      <line x1={X0} y1={BASE} x2={X1} y2={BASE} stroke={LINE} strokeWidth="2" />

      {ticks.map((t, i) => (
        <g key={t.year} className="asmt-guide-tick">
          <line x1={x(t.year)} y1={BASE - 140} x2={x(t.year)} y2={BASE + 22} stroke={LINE} strokeWidth="1.5" />
          <text x={x(t.year)} y={BASE - 176} fontSize="22" fontWeight="700" fill={INK} textAnchor={anchorFor(i)}>
            {t.year}
          </text>
          <text x={x(t.year)} y={BASE - 154} fontSize={SMALL} fill={GUIDE} textAnchor={anchorFor(i)}>
            {t.ages.map(a => `${a.name} ${a.age}`).join(' · ')}
          </text>
          <text
            x={x(t.year)}
            y={BASE + 46 + labelRow(i) * 22}
            fontSize={SMALL}
            fill={GUIDE}
            textAnchor={anchorFor(i)}
          >
            {t.label}
          </text>
        </g>
      ))}

      {segments.map((s, i) => {
        const left = x(s.fromYear);
        const right = x(i === segments.length - 1 ? s.toYear : s.toYear + 1);
        const w = right - left;
        const mid = (left + right) / 2;
        const short = s.shortfall > 0;
        // A stretch too narrow to label is still shaded; its figure lives in
        // the cumulative total below.
        if (w < 60) return short
          ? <rect key={s.fromYear} x={left} y={BASE - 8} width={w} height={16} fill={RED} opacity="0.10" />
          : null;
        // Savings that run out inside this stretch are the headline, not a rate
        // on a balance that is no longer there.
        const big = s.depleted
          ? 'Savings run out'
          : short ? money(s.shortfall) : s.surplus > 0 ? money(s.surplus) : 'Covered';
        const sub = s.depleted
          ? `in ${s.depletedYear}`
          : short
            // One figure only when every year in the stretch is the same; when
            // they differ the range is stated rather than the first year passed
            // off as all of them.
            ? (s.varies ? `${money(s.shortfallMin)} to ${money(s.shortfallMax)} a year short` : 'a year short')
            : s.surplus > 0 ? 'a year to spare' : 'income meets spending';
        const roomy = w >= 150;
        return (
          <g key={s.fromYear} className="asmt-guide-segment">
            {short && <rect x={left} y={BASE - 8} width={w} height={16} fill={RED} opacity="0.10" />}
            <text x={mid} y={BASE - 104} fontSize="32" fontWeight="700" fill={short || s.depleted ? RED : GREEN} textAnchor="middle">
              {big}
            </text>
            <text x={mid} y={BASE - 82} fontSize={SMALL} fill={GUIDE} textAnchor="middle">{sub}</text>
            {roomy && short && (
              <>
                <text x={mid} y={BASE - 60} fontSize={SMALL} fill={GUIDE} textAnchor="middle">
                  {`${plural(s.years, 'year')} · ${money(s.cumulative)}`}
                </text>
                <text x={mid} y={BASE - 40} fontSize={SMALL} fill={GUIDE} textAnchor="middle">
                  {`${pct(s.shareOfLiquid)} of savings`}
                </text>
                {!s.depleted && (
                  <text x={mid} y={BASE - 20} fontSize={SMALL} fill={GUIDE} textAnchor="middle">
                    {`${pct(s.rate)} a year`}
                  </text>
                )}
              </>
            )}
            {roomy && (
              <>
                <text x={mid} y={BASE + 94} fontSize={SMALL} fill={GUIDE} textAnchor="middle">{`need ${money(s.need)}`}</text>
                <text x={mid} y={BASE + 116} fontSize={SMALL} fill={GUIDE} textAnchor="middle">{`income ${money(s.income)}`}</text>
              </>
            )}
          </g>
        );
      })}

      <text x={X0} y={BASE + 172} fontSize={SMALL} fill={GUIDE}>Liquid savings today</text>
      <text x={X0} y={BASE + 204} fontSize="28" fontWeight="700" fill={INK}>{money(liquid)}</text>
      <text x={X1} y={BASE + 172} fontSize={SMALL} fill={GUIDE} textAnchor="end">
        {totals.lastsToHorizon
          ? `From savings through ${years.last}, no growth`
          : `Savings run out in ${totals.depletionYear}, no growth`}
      </text>
      <text
        x={X1}
        y={BASE + 204}
        fontSize="28"
        fontWeight="700"
        fill={totals.lastsToHorizon ? INK : RED}
        textAnchor="end"
      >
        {totals.cumulativeShortfall > 0
          ? `${money(totals.cumulativeShortfall)} · ${pct(totals.shareOfLiquid)}`
          : 'Nothing'}
      </text>

      <text x={X0} y={BASE + 254} fontSize="14" fill={GUIDE} letterSpacing="1.5">FIVE THINGS TO DECIDE</text>
      {decisions.map((d, i) => (
        <text key={d} x={i < 3 ? X0 : X0 + 720} y={BASE + 280 + (i % 3) * 24} fontSize={SMALL} fill={GUIDE}>
          {`${i + 1}. ${d}`}
        </text>
      ))}
    </g>
  );
}
